import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowLeft, Check } from 'lucide-react';
// Quiz Data
const questions = [{
  id: 1,
  title: 'Which kitchen speaks to you?',
  options: [{
    id: 'A',
    label: 'Rustic Warmth',
    image: 'https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?w=800&q=80'
  }, {
    id: 'B',
    label: 'Modern Clean',
    image: 'https://images.unsplash.com/photo-1556909172-54557c7e4fb7?w=800&q=80'
  }]
}, {
  id: 2,
  title: 'Which living space draws you in?',
  options: [{
    id: 'A',
    label: 'Timber & Stone',
    image: 'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=800&q=80'
  }, {
    id: 'B',
    label: 'Light & Airy',
    image: 'https://images.unsplash.com/photo-1600210492486-724fe5c67fb0?w=800&q=80'
  }]
}, {
  id: 3,
  title: 'Your ideal exterior?',
  options: [{
    id: 'A',
    label: 'Mountain Lodge',
    image: 'https://images.unsplash.com/photo-1518780664697-55e3ad937233?w=800&q=80'
  }, {
    id: 'B',
    label: 'Contemporary Lines',
    image: 'https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=800&q=80'
  }]
}, {
  id: 4,
  title: 'Which bathroom feels like a retreat?',
  options: [{
    id: 'A',
    label: 'Natural Materials',
    image: 'https://images.unsplash.com/photo-1600566753190-17f0baa2a6c3?w=800&q=80'
  }, {
    id: 'B',
    label: 'Spa Minimalism',
    image: 'https://images.unsplash.com/photo-1600566753086-00f18fb6b3ea?w=800&q=80'
  }]
}, {
  id: 5,
  title: 'How do you picture your outdoor space?',
  options: [{
    id: 'A',
    label: 'Covered Timber Deck',
    image: 'https://images.unsplash.com/photo-1600585154526-990dced4db0d?w=800&q=80'
  }, {
    id: 'B',
    label: 'Sleek Patio',
    image: 'https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=800&q=80'
  }]
}];
// Result Profiles
const results = {
  mountainCraftsman: {
    title: 'Mountain Craftsman',
    image: 'https://images.unsplash.com/photo-1518780664697-55e3ad937233?w=1200&q=80',
    description: 'You are drawn to the timeless appeal of traditional mountain architecture. Heavy timber frames, natural stone, and warm, earthy tones define your ideal space. You value craftsmanship and spaces that feel deeply connected to the surrounding forest and landscape.'
  },
  modernAlpine: {
    title: 'Modern Alpine',
    image: 'https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=1200&q=80',
    description: 'You appreciate clean lines, expansive glass, and minimalist luxury. Your ideal home brings the outdoors in through massive windows while maintaining a sleek, uncluttered interior. You prefer a refined palette that lets the mountain views take center stage.'
  },
  mountainModernBlend: {
    title: 'Mountain Modern Blend',
    image: 'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=1200&q=80',
    description: 'You want the best of both worlds. You appreciate the warmth and texture of rustic materials like reclaimed wood and stone, but prefer them applied in clean, contemporary ways. Your style balances cozy mountain charm with sophisticated, airy design.'
  }
};
export function StyleQuizPage() {
  const [currentStep, setCurrentStep] = useState(0); // 0-4: Questions, 5: Email, 6: Results
  const [answers, setAnswers] = useState<string[]>([]);
  const [selectedOption, setSelectedOption] = useState<string | null>(null);
  const [email, setEmail] = useState('');
  const [finalResult, setFinalResult] = useState<keyof typeof results | null>(null);
  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  const handleOptionSelect = (optionId: string) => {
    if (selectedOption) return; // Prevent double clicks
    setSelectedOption(optionId);
    // Brief delay to show selection before advancing
    setTimeout(() => {
      const newAnswers = [...answers, optionId];
      setAnswers(newAnswers);
      setSelectedOption(null);
      if (currentStep < questions.length - 1) {
        setCurrentStep(prev => prev + 1);
      } else {
        // Calculate result before moving to email step
        calculateResult(newAnswers);
        setCurrentStep(5); // Move to email capture
      }
    }, 600);
  };
  const calculateResult = (finalAnswers: string[]) => {
    const countA = finalAnswers.filter(a => a === 'A').length;
    const countB = finalAnswers.filter(a => a === 'B').length;
    if (countA >= 4) {
      setFinalResult('mountainCraftsman');
    } else if (countB >= 4) {
      setFinalResult('modernAlpine');
    } else {
      setFinalResult('mountainModernBlend');
    }
  };
  const handleEmailSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (email) {
      setCurrentStep(6); // Move to results
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    }
  };
  // Animation variants
  const pageVariants = {
    initial: {
      opacity: 0,
      y: 20
    },
    animate: {
      opacity: 1,
      y: 0
    },
    exit: {
      opacity: 0,
      y: -20
    }
  };
  return <div className="min-h-screen bg-[#1A1A1A] font-sans selection:bg-[#A07656] selection:text-white flex flex-col" data-id="element-474">
      {/* Header */}
      <header className="px-6 py-6 lg:px-12 flex justify-between items-center relative z-20" data-id="element-475">
        <Link to="/" data-id="element-476">
          <img src="https://cdn.magicpatterns.com/uploads/rsVSuidRepyz9oxNYVg3rK/Iron_Ridge_Logo.png" alt="Iron Ridge Custom Homes" className="h-10 lg:h-12 w-auto brightness-0 invert" data-id="element-477" />
        </Link>
        <Link to="/" className="flex items-center gap-2 text-xs uppercase tracking-widest font-medium text-[#595959] hover:text-[#A07656] transition-colors" data-id="element-478">
          <ArrowLeft className="w-4 h-4" data-id="element-479" />
          <span className="hidden sm:inline" data-id="element-480">Back to Home</span>
        </Link>
      </header>

      {/* Main Content Area */}
      <main className="flex-grow flex flex-col items-center justify-center px-4 py-8 relative z-10" data-id="element-481">
        <AnimatePresence mode="wait" data-id="element-482">
          {/* STATE 1: QUIZ QUESTIONS */}
          {currentStep < 5 && <motion.div key={`question-${currentStep}`} variants={pageVariants} initial="initial" animate="animate" exit="exit" transition={{
          duration: 0.4
        }} className="w-full max-w-5xl mx-auto" data-id="element-483">
              {/* Progress Bar */}
              <div className="mb-12 text-center" data-id="element-484">
                <span className="text-[#A07656] text-xs font-bold uppercase tracking-[0.2em] mb-4 block" data-id="element-485">
                  Question {currentStep + 1} of {questions.length}
                </span>
                <div className="w-full max-w-md mx-auto h-1 bg-[#252525] rounded-full overflow-hidden" data-id="element-486">
                  <motion.div className="h-full bg-[#A07656]" initial={{
                width: `${currentStep / questions.length * 100}%`
              }} animate={{
                width: `${(currentStep + 1) / questions.length * 100}%`
              }} transition={{
                duration: 0.5
              }} data-id="element-487" />
                </div>
              </div>

              <h1 className="text-3xl md:text-4xl lg:text-5xl font-serif font-bold text-[#F5F3F0] text-center mb-12" data-id="element-488">
                {questions[currentStep].title}
              </h1>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-10" data-id="element-489">
                {questions[currentStep].options.map(option => {
              const isSelected = selectedOption === option.id;
              return <motion.button key={option.id} onClick={() => handleOptionSelect(option.id)} disabled={selectedOption !== null} whileHover={selectedOption === null ? {
                scale: 1.02
              } : {}} whileTap={selectedOption === null ? {
                scale: 0.98
              } : {}} className={`relative group overflow-hidden aspect-[4/3] rounded-sm text-left transition-all duration-300 ${isSelected ? 'ring-4 ring-[#A07656] ring-offset-4 ring-offset-[#1A1A1A]' : ''} ${selectedOption !== null && !isSelected ? 'opacity-50 grayscale' : ''}`} data-id="element-490">
                      <img src={option.image} alt={option.label} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" data-id="element-491" />
                      <div className="absolute inset-0 bg-gradient-to-t from-[#1A1A1A]/90 via-[#1A1A1A]/20 to-transparent" data-id="element-492" />

                      <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8 flex justify-between items-end" data-id="element-493">
                        <span className="text-xl md:text-2xl font-serif font-bold text-white" data-id="element-494">
                          {option.label}
                        </span>

                        {isSelected && <motion.div initial={{
                    scale: 0
                  }} animate={{
                    scale: 1
                  }} className="w-8 h-8 rounded-full bg-[#A07656] flex items-center justify-center" data-id="element-495">
                            <Check className="w-5 h-5 text-white" data-id="element-496" />
                          </motion.div>}
                      </div>
                    </motion.button>;
            })}
              </div>
            </motion.div>}

          {/* STATE 2: EMAIL CAPTURE */}
          {currentStep === 5 && <motion.div key="email-capture" variants={pageVariants} initial="initial" animate="animate" exit="exit" transition={{
          duration: 0.5
        }} className="w-full max-w-xl mx-auto text-center" data-id="element-497">
              <div className="w-20 h-20 mx-auto border-2 border-[#A07656]/30 rounded-full flex items-center justify-center mb-8 bg-[#252525]/50" data-id="element-498">
                <Check className="w-8 h-8 text-[#A07656]" data-id="element-499" />
              </div>

              <h2 className="text-4xl md:text-5xl font-serif font-bold text-[#F5F3F0] mb-6" data-id="element-500">
                Your Style Profile is Ready!
              </h2>

              <p className="text-[#D4CFC8]/80 text-lg leading-relaxed mb-10" data-id="element-501">
                Enter your email to unlock your Architectural Personality
                results and receive a curated inspiration guide tailored to your
                taste.
              </p>

              <form onSubmit={handleEmailSubmit} className="space-y-6" data-id="element-502">
                <div data-id="element-503">
                  <input type="email" required value={email} onChange={e => setEmail(e.target.value)} placeholder="Enter your email address" className="w-full bg-transparent border-b-2 border-[#595959] focus:border-[#A07656] py-4 text-center text-xl text-[#F5F3F0] placeholder-[#595959]/50 outline-none transition-colors rounded-none appearance-none" data-id="element-504" />
                </div>

                <button type="submit" className="w-full group relative inline-flex items-center justify-center px-10 py-5 bg-[#A07656] text-white overflow-hidden transition-all duration-300 hover:bg-[#B8896A] hover:shadow-[0_0_30px_rgba(160,118,86,0.3)]" data-id="element-505">
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/15 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700" data-id="element-506" />
                  <span className="relative z-10 text-sm tracking-[0.2em] uppercase font-semibold" data-id="element-507">
                    Reveal My Style
                  </span>
                </button>

                <p className="text-xs text-[#595959] uppercase tracking-widest mt-4" data-id="element-508">
                  We respect your privacy. No spam, ever.
                </p>
              </form>
            </motion.div>}

          {/* STATE 3: RESULTS */}
          {currentStep === 6 && finalResult && <motion.div key="results" variants={pageVariants} initial="initial" animate="animate" transition={{
          duration: 0.6
        }} className="w-full max-w-4xl mx-auto" data-id="element-509">
              <div className="text-center mb-10" data-id="element-510">
                <span className="text-[#A07656] text-xs font-bold uppercase tracking-[0.2em] mb-4 block" data-id="element-511">
                  Your Architectural Personality
                </span>
                <h2 className="text-5xl md:text-6xl font-serif font-bold text-[#F5F3F0]" data-id="element-512">
                  {results[finalResult].title}
                </h2>
              </div>

              <div className="bg-[#252525] border border-[#595959]/30 overflow-hidden shadow-2xl" data-id="element-513">
                <div className="relative h-64 md:h-96 w-full" data-id="element-514">
                  <img src={results[finalResult].image} alt={results[finalResult].title} className="w-full h-full object-cover" data-id="element-515" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#252525] to-transparent opacity-80" data-id="element-516" />
                </div>

                <div className="p-8 md:p-12 text-center relative -mt-16 z-10" data-id="element-517">
                  <p className="text-[#D4CFC8] text-lg md:text-xl leading-relaxed max-w-2xl mx-auto mb-12" data-id="element-518">
                    {results[finalResult].description}
                  </p>

                  <div className="space-y-6" data-id="element-519">
                    <h3 className="text-2xl font-serif font-semibold text-[#F5F3F0]" data-id="element-520">
                      Ready to bring this vision to life?
                    </h3>

                    <div className="flex flex-col sm:flex-row items-center justify-center gap-4" data-id="element-521">
                      <Link to="/begin" className="w-full sm:w-auto group relative inline-flex items-center justify-center px-10 py-4 bg-[#A07656] text-white overflow-hidden transition-all duration-300 hover:bg-[#B8896A]" data-id="element-522">
                        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/15 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700" data-id="element-523" />
                        <span className="relative z-10 text-sm tracking-[0.2em] uppercase font-semibold" data-id="element-524">
                          Begin Your Journey
                        </span>
                      </Link>

                      <Link to="/" className="w-full sm:w-auto px-10 py-4 border border-[#595959] text-[#D4CFC8] hover:text-white hover:border-[#A07656] transition-colors text-sm tracking-[0.2em] uppercase font-semibold" data-id="element-525">
                        Back to Home
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>}
        </AnimatePresence>
      </main>

      {/* Subtle background texture */}
      <div className="fixed inset-0 pointer-events-none z-0 opacity-20 mix-blend-overlay" data-id="element-526">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_0%,rgba(26,26,26,0.8)_100%)]" data-id="element-527" />
      </div>
    </div>;
}